import type { Concept } from '@yuan-math/curriculum';

/**
 * The university probability-and-statistics pathway (Grade 13).
 *
 * School statistics is a collection of techniques: find the mean, draw the box
 * plot, quote r, apply the 68–95–99.7 rule. Each one is a correct thing to do
 * to a set of numbers you already have. None of them says anything about the
 * numbers you *don't* have — which is the only reason anybody collects data.
 *
 * These twenty concepts are the bridge from the first thing to the second, and
 * the bridge has exactly one plank: a random variable. Once a quantity that
 * varies has a distribution, it has a mean and a standard deviation of its own;
 * once a *sample mean* is seen as such a quantity, it has a distribution too,
 * and the width of that distribution is what a confidence interval and a
 * p-value are both measuring. Everything from `sampling-distributions` onwards
 * is that one observation applied.
 *
 * The orderings that are decisions rather than conventions:
 *
 * `sample-spaces` comes first and is not a repeat of Grade 7. Grade 7 computes
 * favourable ÷ total on a die, which works because the outcomes of a die are
 * equally likely — and a student who has only ever seen that case reads
 * "probability" as a synonym for "proportion of a list". The first thing this
 * concept does is exhibit a sample space whose outcomes are *not* equally
 * likely (the total of two dice), because that is where the counting recipe
 * silently stops working and the axioms have to take over.
 *
 * `counting-probability` depends on `combinatorics-intro` and is separate from
 * it. Knowing that there are ⁵²C₅ poker hands is combinatorics; knowing that
 * the probability of a flush is the ratio of two such counts requires the
 * outcomes to be equally likely, and that condition is the entire content of
 * this concept. It is placed after `sample-spaces` for that reason.
 *
 * `independence` comes *after* `conditional-probability`, not before it, which
 * is the reverse of most treatments. Independence is normally introduced as a
 * multiplication rule — P(A and B) = P(A)P(B) — and a rule stated first is a
 * rule applied everywhere: students multiply through problems about cards drawn
 * without replacement for years afterwards. Defined as P(A | B) = P(A), it says
 * what it means: knowing B happened tells you nothing about A. The
 * multiplication rule is then a consequence, and its precondition is visible.
 *
 * `bayes-theorem` needs nothing beyond conditional probability, and is a
 * concept of its own because the arithmetic is not the difficulty. The
 * difficulty is that P(disease | positive) and P(positive | disease) are
 * different numbers and the language barely distinguishes them, which is why
 * the lesson is built around a screening test where they differ by a factor of
 * fifty.
 *
 * `expected-value`, `random-variable-variance` and `random-variable-sd` are
 * three concepts rather than one. They are three formulas a student can execute
 * in a single sitting, and separating them is not about difficulty — it is
 * about what each one *is*. The mean of a random variable is a weighted
 * average of things that have not happened; the variance is the same weighted
 * average applied to squared distances and comes out in squared units nobody
 * can interpret; the standard deviation is what returns those units and is the
 * only one of the three that combines by a rule worth memorising (σ² adds for
 * independent variables, σ does not). Taught as one topic, the last of those
 * facts is a footnote; taught as three, it is a concept with its own practice.
 *
 * `continuous-random-variables` does **not** depend on `integrals`. It could:
 * a density is integrated in every university course, and this one says so. But
 * the idea — that probability is area under a curve and that P(X = x) is
 * therefore zero — is completely visible on a uniform and a triangular density,
 * where the area is a rectangle and a triangle. Requiring calculus here would
 * gate the entire statistics pathway behind the *other* Grade 12 pathway, for a
 * technique this course never uses: the normal areas come from a table.
 *
 * `normal-random-variable` depends on the Grade 11 `normal-distribution` and
 * adds the two things that course could not do. Grade 11 has the empirical
 * rule, which answers questions about 1, 2 and 3 standard deviations and
 * nothing in between; here z = 1.37 is as answerable as z = 2, the question can
 * run backwards from a percentile to a value, and a binomial with enough trials
 * can be handed to the normal curve.
 *
 * `sampling-distributions` is the hinge of the whole course, and it is the
 * concept students skip. Everything after it is arithmetic on σ/√n, so a
 * student who has not understood which quantity is varying computes confidence
 * intervals correctly for a term and cannot say what the interval is about. It
 * depends on `random-variable-sd` because the central limit theorem is a claim
 * about the mean and standard deviation of a *sum*, and on `sampling` because
 * the Grade 7 question — what can this handful tell me about the population? —
 * is the one being answered at last.
 *
 * `hypothesis-testing` comes after `confidence-intervals` deliberately. A test
 * reports a decision, and a decision is the easiest possible thing to produce
 * without understanding: reject, don't reject, done. An interval reports a
 * range of plausible values, which cannot be produced without knowing what is
 * varying and by how much. Taught in that order, the p-value is the interval
 * seen from the other end rather than a rule about 0.05.
 *
 * `correlation` and `linear-regression` are split, where Grade 11 taught them
 * together, because the split is what makes the algebra mean anything: the
 * least-squares slope is b = r·(s_y/s_x), so the regression line is literally
 * the correlation, rescaled into the units of the data. That sentence is
 * unavailable to a course where r arrived as "the strength of the fit" in the
 * same breath as the line.
 */
export const STATISTICS_CONCEPTS: Concept[] = [
  /* ------------------------------------------------- probability, properly */
  {
    id: 'sample-spaces',
    title: 'Sample Spaces',
    summary: 'Writing down everything that could happen, before asking how likely any of it is.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['compound-probability'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'probability-events',
    title: 'Events and the Addition Rule',
    summary: 'Combining events with "and", "or" and "not" — and the overlap that has to be subtracted back out.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['sample-spaces'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'counting-probability',
    title: 'Counting to Find Probability',
    summary: 'Probabilities too big to list, found as a ratio of two counts — and the condition that makes that legal.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['sample-spaces', 'combinatorics-intro'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'conditional-probability',
    title: 'Conditional Probability',
    summary: 'What a probability becomes once you know something — and which number goes underneath when it does.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['probability-events', 'two-way-tables'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'independence',
    title: 'Independence',
    summary: 'When knowing one thing tells you nothing about another — and the multiplication rule that follows only then.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['conditional-probability'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'bayes-theorem',
    title: "Bayes' Theorem",
    summary: 'Turning a conditional probability around, and the screening test that catches almost nobody it names.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['conditional-probability'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ---------------------------------------------------- random variables */
  {
    id: 'discrete-random-variables',
    title: 'Discrete Random Variables',
    summary: 'A number decided by chance, described completely by the probability of each value it can take.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['probability-events', 'probability-distributions'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'expected-value',
    title: 'Expected Value',
    summary: 'The long-run average of something that has not happened yet — and why it need not be a value it can take.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['discrete-random-variables'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'random-variable-variance',
    title: 'Variance',
    summary: 'How far a random variable typically lands from its mean, measured in squared units on purpose.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['expected-value', 'standard-deviation'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'random-variable-sd',
    title: 'Standard Deviation and Combining Variables',
    summary: 'Getting the units back, and the one rule everybody gets wrong: variances add, standard deviations do not.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['random-variable-variance', 'independence'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'continuous-random-variables',
    title: 'Continuous Random Variables',
    summary: 'A quantity with no gaps between its values, where probability is an area and any single value has none.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['discrete-random-variables', 'histograms'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* -------------------------------------------- the distributions worth knowing */
  {
    id: 'binomial-distribution',
    title: 'The Binomial Distribution',
    summary: 'Counting successes in a fixed number of independent tries — the distribution four conditions produce.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['expected-value', 'independence', 'combinatorics-intro'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'geometric-distribution',
    title: 'The Geometric Distribution',
    summary: 'Waiting for the first success instead of counting successes — the same trials, a different question.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['binomial-distribution'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'poisson-distribution',
    title: 'The Poisson Distribution',
    summary: 'Counting events in an interval when there is no fixed number of trials to count out of.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['binomial-distribution'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'normal-random-variable',
    title: 'Normal Random Variables',
    summary: 'Reading any area under the bell curve, running the question backwards, and lending the curve to the binomial.',
    strand: 'probability',
    levels: ['university'],
    prerequisites: ['continuous-random-variables', 'normal-distribution'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ------------------------------------------------------------- inference */
  {
    id: 'sampling-distributions',
    title: 'Sampling Distributions',
    summary: 'The sample mean is itself a random variable — and knowing its spread is what makes statistics possible.',
    strand: 'statistics',
    levels: ['university'],
    prerequisites: ['random-variable-sd', 'normal-random-variable', 'sampling'],
    status: 'available',
    estimatedMinutes: 50,
  },
  {
    id: 'confidence-intervals',
    title: 'Confidence Intervals',
    summary: 'Reporting a range instead of a number, and saying honestly what the confidence is confidence in.',
    strand: 'statistics',
    levels: ['university'],
    prerequisites: ['sampling-distributions'],
    status: 'available',
    estimatedMinutes: 50,
  },
  {
    id: 'hypothesis-testing',
    title: 'Hypothesis Testing',
    summary: 'Asking whether the data would be surprising if nothing were going on — and what a small p-value does not say.',
    strand: 'statistics',
    levels: ['university'],
    prerequisites: ['confidence-intervals'],
    status: 'available',
    estimatedMinutes: 50,
  },
  {
    id: 'correlation',
    title: 'Correlation',
    summary: 'Measuring linear association as an average of products of z-scores, which is why it has no units.',
    strand: 'statistics',
    levels: ['university'],
    prerequisites: ['regression', 'standard-deviation'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'linear-regression',
    title: 'Least-Squares Regression',
    summary: 'The line, computed rather than drawn: its slope is the correlation wearing the units of the data.',
    strand: 'statistics',
    levels: ['university'],
    prerequisites: ['correlation'],
    status: 'available',
    estimatedMinutes: 50,
  },
];
